<svelte:options runes={false} />
<script>
  // Maker-capture heatmap (TASK-20260823-twap-strategies-tca PR-C2, D9): day-of-week × hour (UTC)
  // maker share of the fills, the MonthlyReturnsHeatmap grammar in inline SVG so cells are DOM nodes
  // (focusable, labelled, token-themed). A null cell (no fills that hour) is hatched and excluded from
  // the color domain; a cell with fills but no maker flag is outlined-hatched and says so. The readout
  // and the table fallback carry the exact figures, so the color ramp is never the only channel.
  // The region owns its loading / error / empty states — it loads independently of the parent list.
  import { DAY_LABELS, HOURS } from '../../../lib/twapTab.js';

  export let grid = null;        // from heatmapGrid(cells)
  export let loading = false;
  export let error = null;
  export let title = 'Maker capture by day × hour (UTC)';
  export let emptyText = 'No fills to map yet.';

  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  const uid = `hm-${Math.random().toString(36).slice(2, 8)}`;
  const LABEL_W = 34;
  const HEADER_H = 16;
  const GAP = 1;
  let width = 480;
  let active = null;
  let pinned = null;

  $: rows = grid?.rows ?? [];
  $: hasData = Boolean(grid?.hasData);
  $: w = Math.max(280, width);
  $: cellW = (w - LABEL_W) / 24;
  $: cellH = Math.max(14, Math.min(24, cellW));
  $: h = HEADER_H + cellH * 7 + 4;

  // Sequential ramp on the success hue: 0 % maker → faint, 100 % → solid. Fills weight nothing here
  // (the readout carries the count) so a one-fill hour never looks "bigger" than a fifty-fill hour.
  const alphaOf = (pct) => (0.12 + 0.76 * Math.max(0, Math.min(100, pct)) / 100).toFixed(3);

  function key(d, hr) { return `${d}-${hr}`; }
  function togglePin(d, hr) { const k = key(d, hr); pinned = pinned === k ? null : k; active = pinned ?? k; }
  function onKey(e, d, hr) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePin(d, hr); }
  }
  function cellText(d, hr) {
    const c = rows[d]?.[hr] ?? null;
    const when = `${DAY_LABELS[d]} ${String(hr).padStart(2, '0')}:00 UTC`;
    if (!c) return `${when}: no fills`;
    const fills = `${c.fills} fill${c.fills === 1 ? '' : 's'}`;
    return isNum(c.makerFillPct) ? `${when}: ${c.makerFillPct.toFixed(1)} % maker over ${fills}` : `${when}: ${fills}, maker share unknown`;
  }
  $: activeText = active ? cellText(...active.split('-').map(Number)) : 'Hover or focus a cell for its figures.';
  $: summary = hasData ? `${title}: ${grid.fills} fills mapped.` : emptyText;
</script>

<div class="hm" bind:clientWidth={width}>
  <span class="hm-title">{title}</span>
  {#if loading && !grid}
    <div class="hm-skeleton" aria-busy="true">Loading heatmap…</div>
  {:else if error}
    <p class="hm-error" role="alert">{error}</p>
  {:else if !hasData}
    <p class="hm-empty">{emptyText}</p>
  {:else}
    <svg class="hm-svg" viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label={summary}>
      <defs>
        <pattern id={`${uid}-hatch`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" class="hm-hatch" />
        </pattern>
      </defs>
      {#each HOURS as hr}
        {#if hr % 3 === 0}
          <text x={LABEL_W + cellW * (hr + 0.5)} y={HEADER_H - 5} class="hm-tick" text-anchor="middle">{String(hr).padStart(2, '0')}</text>
        {/if}
      {/each}
      {#each DAY_LABELS as day, d}
        <text x={LABEL_W - 6} y={HEADER_H + cellH * (d + 0.5) + 3} class="hm-tick" text-anchor="end">{day}</text>
        {#each HOURS as hr}
          {@const c = rows[d]?.[hr] ?? null}
          {@const x = LABEL_W + cellW * hr + GAP}
          {@const y = HEADER_H + cellH * d + GAP}
          {#if c}
            <rect {x} {y} width={Math.max(1, cellW - 2 * GAP)} height={cellH - 2 * GAP} rx="2"
              class={`hm-cell ${isNum(c.makerFillPct) ? 'hm-cell-known' : 'hm-cell-unknown'}`}
              class:hm-cell-active={active === key(d, hr)}
              style={isNum(c.makerFillPct) ? `fill-opacity: ${alphaOf(c.makerFillPct)}` : ''}
              fill={isNum(c.makerFillPct) ? undefined : `url(#${uid}-hatch)`}
              tabindex="0" role="button" aria-label={cellText(d, hr)} aria-pressed={pinned === key(d, hr)}
              on:mouseenter={() => (active = key(d, hr))} on:mouseleave={() => (active = pinned)}
              on:focus={() => (active = key(d, hr))} on:blur={() => (active = pinned)}
              on:click={() => togglePin(d, hr)} on:keydown={(e) => onKey(e, d, hr)}>
              <title>{cellText(d, hr)}</title>
            </rect>
          {:else}
            <rect {x} {y} width={Math.max(1, cellW - 2 * GAP)} height={cellH - 2 * GAP} rx="2" class="hm-cell-null" fill={`url(#${uid}-hatch)`} aria-hidden="true" />
          {/if}
        {/each}
      {/each}
    </svg>
    <div class="hm-foot">
      <span class="hm-legend" aria-hidden="true">
        <span class="hm-legend-label">0 %</span>
        <span class="hm-ramp"></span>
        <span class="hm-legend-label">100 % maker</span>
        <span class="hm-legend-swatch"></span>
        <span class="hm-legend-label">hatched = no fills</span>
      </span>
      <span class="hm-readout num" aria-live="polite">{activeText}</span>
    </div>
    <details class="hm-data">
      <summary>View data as table</summary>
      <div class="hm-scroll">
        <table>
          <thead><tr><th>Day (UTC)</th>{#each HOURS as hr}<th class="num">{String(hr).padStart(2, '0')}</th>{/each}</tr></thead>
          <tbody>
            {#each DAY_LABELS as day, d}
              <tr><th>{day}</th>
                {#each HOURS as hr}
                  {@const c = rows[d]?.[hr] ?? null}
                  <td class="num" title={cellText(d, hr)}>{c ? (isNum(c.makerFillPct) ? `${c.makerFillPct.toFixed(0)} %` : `? (${c.fills})`) : ''}</td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </details>
  {/if}
</div>

<style>
  .hm { display: flex; flex-direction: column; gap: 0.3rem; width: 100%; min-width: 0; }
  .num { font-variant-numeric: tabular-nums; }
  .hm-title { font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); text-transform: uppercase; letter-spacing: 0.04em; }
  .hm-skeleton, .hm-empty { margin: 0; color: var(--text-muted, #6a6b6d); font-size: var(--font-size-sm, 0.85rem); }
  .hm-error { margin: 0; color: var(--color-danger, #ff3b3b); font-size: var(--font-size-sm, 0.85rem); }
  .hm-svg { display: block; border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); background: var(--bg-secondary, #111214); }
  .hm-tick { fill: var(--text-secondary, #8a8b8d); font-size: 9px; font-variant-numeric: tabular-nums; }
  .hm-hatch { stroke: var(--border-tertiary, #3a3b3e); stroke-width: 1; }
  .hm-cell { cursor: pointer; outline: none; transition: fill-opacity var(--transition-fast, 120ms ease); }
  .hm-cell-known { fill: var(--color-success, #00ff88); }
  .hm-cell-unknown { stroke: var(--color-warning, #ffaa00); stroke-width: 1; }
  .hm-cell-null { stroke: none; }
  .hm-cell-active, .hm-cell:focus-visible { stroke: var(--text-primary, #fff); stroke-width: 1.5; }
  .hm-foot { display: flex; flex-wrap: wrap; gap: 0.4rem 1rem; align-items: center; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); }
  .hm-legend { display: inline-flex; align-items: center; gap: 0.35rem; }
  .hm-legend-label { color: var(--text-muted, #6a6b6d); }
  .hm-ramp { display: inline-block; width: 72px; height: 8px; border-radius: 2px; background: linear-gradient(to right, color-mix(in srgb, var(--color-success, #00ff88) 12%, transparent), var(--color-success, #00ff88)); }
  .hm-legend-swatch { display: inline-block; width: 14px; height: 8px; border-radius: 2px; margin-left: 0.6rem; background: repeating-linear-gradient(45deg, var(--border-tertiary, #3a3b3e) 0 1px, transparent 1px 4px); }
  .hm-readout { min-height: 1.2em; }
  .hm-data summary { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); cursor: pointer; }
  .hm-scroll { overflow-x: auto; }
  .hm-data table { border-collapse: collapse; font-size: var(--font-size-xs, 0.65rem); margin-top: 0.25rem; }
  .hm-data th, .hm-data td { padding: 1px 0.3rem; border-bottom: 1px solid var(--border-secondary, #2a2b2e); color: var(--text-secondary, #8a8b8d); text-align: right; white-space: nowrap; }
  .hm-data th:first-child { text-align: left; }
  @media (prefers-reduced-motion: reduce) { .hm-cell { transition: none; } }
</style>
